import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Flame, Boxes } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function MasterList({ title, endpoint, testId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  const load = async () => {
    const { data } = await api.get(endpoint);
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post(endpoint, { name: name.trim() });
      setName(""); load();
      toast.success("Added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`${endpoint}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat">
      <div className="p-4 border-b border-gray-200">
        <div className="small-label text-gray-500">Master</div>
        <div className="font-heading font-bold text-xl">{title}</div>
      </div>
      <form onSubmit={add} className="p-4 border-b border-gray-200 flex gap-2" data-testid={`${testId}-form`}>
        <input className="input-lg flex-1" placeholder={`Add ${title.slice(0, -1)}`}
          value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testId}-input`} />
        <button className="btn-primary flex items-center gap-1" data-testid={`${testId}-add`}><Plus size={14} /> Add</button>
      </form>
      <ul className="divide-y divide-gray-200 max-h-[360px] overflow-auto">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`${testId}-item-${it.name}`}>
            <span className="font-medium text-sm">{it.name}</span>
            {isAdmin && (
              <button onClick={() => del(it.id)} className="text-gray-400 hover:text-[#B91C1C]">
                <Trash2 size={15} />
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="px-4 py-6 text-center text-gray-400 text-sm">None yet</li>}
      </ul>
    </div>
  );
}

function UsersSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [holders, setHolders] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "operator", holder_id: "" });

  const load = async () => {
    const [u, h] = await Promise.all([api.get("/auth/users"), api.get("/cash-holders")]);
    setUsers(u.data); setHolders(h.data);
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin") return null;

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.holder_id) delete payload.holder_id;
      await api.post("/auth/register", payload);
      setForm({ email: "", name: "", password: "", role: "operator", holder_id: "" });
      load(); toast.success("User created");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/auth/users/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const holderName = (id) => {
    const h = holders.find((x) => x.id === id);
    return h ? `${h.name}${h.location ? ` (${h.location})` : ""}` : null;
  };

  return (
    <div className="card-flat">
      <div className="p-4 border-b border-gray-200">
        <div className="small-label text-gray-500">Team</div>
        <div className="font-heading font-bold text-xl">Users</div>
      </div>
      <form onSubmit={add} className="p-4 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2" data-testid="user-form">
        <input required className="input-lg" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required type="email" className="input-lg" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input required type="password" className="input-lg" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <select className="input-lg" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="operator">operator</option>
          <option value="manager">manager</option>
          <option value="stock_viewer">stock viewer (read-only)</option>
          <option value="sales">sales / marketing</option>
          <option value="admin">admin</option>
        </select>
        <select className="input-lg" value={form.holder_id} onChange={(e) => setForm({ ...form, holder_id: e.target.value })} data-testid="user-holder">
          <option value="">No cash holder</option>
          {holders.map((h) => <option key={h.id} value={h.id}>{h.name}{h.location ? ` — ${h.location}` : ""}</option>)}
        </select>
        <button className="btn-primary" data-testid="user-add">Add User</button>
      </form>
      <ul className="divide-y divide-gray-200">
        {users.map((u) => (
          <li key={u.id} className="flex items-center justify-between px-4 py-2.5">
            <div>
              <div className="font-semibold text-sm">
                {u.name} <span className="small-label ml-2">{u.role}</span>
                {u.holder_id && <span className="small-label ml-2 text-[#15803D]">cash: {holderName(u.holder_id) || "—"}</span>}
              </div>
              <div className="text-xs text-gray-500 font-mono">{u.email}</div>
            </div>
            {u.id !== user.id && (
              <button onClick={() => del(u.id)} className="text-gray-400 hover:text-[#B91C1C]"><Trash2 size={15} /></button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Masters() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <MasterList title="Brands" endpoint="/brands" testId="brand" />
      <MasterList title="Sizes" endpoint="/sizes" testId="size" />
      <GodownList />
      <MasterList title="Expense Categories" endpoint="/expense-categories" testId="expense-cat" />
      <CashHolders />
      <div className="lg:col-span-3">
        <UsersSection />
      </div>
    </div>
  );
}

function GodownList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "standard" });

  const load = async () => {
    const { data } = await api.get("/godowns");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/godowns", { name: form.name.trim(), kind: form.kind });
      setForm({ name: "", kind: "standard" });
      load();
      toast.success(form.kind === "bullet" ? "Bullet godown added (stores LPG in KG)" : "Godown added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleKind = async (g) => {
    const next = g.kind === "bullet" ? "standard" : "bullet";
    if (!window.confirm(`Change "${g.name}" to a ${next === "bullet" ? "Bullet (KG-based)" : "Standard (cylinder)"} godown?`)) return;
    try {
      await api.patch(`/godowns/${g.id}`, { kind: next });
      load();
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this godown? Only allowed if no transaction references it.")) return;
    try { await api.delete(`/godowns/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat">
      <div className="p-4 border-b border-gray-200">
        <div className="small-label text-gray-500">Master</div>
        <div className="font-heading font-bold text-xl">Godowns</div>
        <p className="text-xs text-gray-500 mt-1">
          Standard godowns hold cylinders. <span className="text-[#B45309] font-semibold">Bullet</span> godowns store LPG in KG (used for bulk purchase / refill).
        </p>
      </div>
      {isAdmin && (
        <form onSubmit={add} className="p-4 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-12 gap-2" data-testid="godown-form">
          <input className="input-lg sm:col-span-6" placeholder="Godown name e.g. Pune Bullet"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="godown-name" />
          <select className="input-lg sm:col-span-4" value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="godown-kind">
            <option value="standard">Standard · cylinders</option>
            <option value="bullet">Bullet · KG (bulk LPG)</option>
          </select>
          <button className="btn-primary sm:col-span-2 flex items-center justify-center gap-1" data-testid="godown-add">
            <Plus size={14} /> Add
          </button>
        </form>
      )}
      <ul className="divide-y divide-gray-200 max-h-[360px] overflow-auto">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`godown-item-${it.name}`}>
            <div className="flex items-center gap-2">
              {it.kind === "bullet"
                ? <Flame size={16} className="text-[#B45309]" />
                : <Boxes size={16} className="text-gray-400" />}
              <div>
                <div className="font-medium text-sm">{it.name}</div>
                <div className="text-xs text-gray-500">
                  {it.kind === "bullet" ? "Bullet · stores LPG in KG" : "Standard · stores cylinders"}
                </div>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleKind(it)}
                  className="text-xs text-gray-500 hover:text-black border border-gray-200 rounded-sm px-2 py-1 font-semibold"
                  title={`Convert to ${it.kind === "bullet" ? "standard" : "bullet"}`}
                  data-testid={`godown-toggle-${it.name}`}
                >
                  {it.kind === "bullet" ? "→ Standard" : "→ Bullet"}
                </button>
                <button onClick={() => del(it.id)} className="text-gray-400 hover:text-[#B91C1C]" data-testid={`godown-del-${it.name}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="px-4 py-6 text-center text-gray-400 text-sm">None yet</li>}
      </ul>
    </div>
  );
}

function CashHolders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "" });

  const load = async () => { const { data } = await api.get("/cash-holders"); setItems(data); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/cash-holders", form);
      setForm({ name: "", location: "" }); load(); toast.success("Added");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/cash-holders/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat">
      <div className="p-4 border-b border-gray-200">
        <div className="small-label text-gray-500">Master</div>
        <div className="font-heading font-bold text-xl">Cash Holders</div>
      </div>
      {isAdmin && (
        <form onSubmit={add} className="p-4 border-b border-gray-200 grid grid-cols-3 gap-2">
          <input className="input-lg h-10 col-span-1" placeholder="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holder-name" />
          <input className="input-lg h-10 col-span-1" placeholder="Location" value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <button className="btn-primary h-10 flex items-center gap-1" data-testid="holder-add">
            <Plus size={14} /> Add
          </button>
        </form>
      )}
      <ul className="divide-y divide-gray-200 max-h-[360px] overflow-auto">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            <div>
              <div className="font-semibold text-sm">{it.name}</div>
              <div className="text-xs text-gray-500">{it.location || "—"}</div>
            </div>
            {isAdmin && (
              <button onClick={() => del(it.id)} className="text-gray-400 hover:text-[#B91C1C]">
                <Trash2 size={15} />
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="px-4 py-6 text-center text-gray-400 text-sm">None yet</li>}
      </ul>
    </div>
  );
}
